import { useEffect, useState, useRef } from 'react';
import { LayoutManager } from './components/UI/LayoutManager';
import { LottieAvatar } from './components/Avatar/LottieAvatar';
import { MicrophoneButton } from './components/UI/MicrophoneButton';
import { InfoButton } from './components/UI/InfoButton';
import { AcademicProgressButton } from './components/UI/AcademicProgressButton';
import { HomeButton } from './components/UI/HomeButton';
import { ActivitiesButton } from './components/UI/ActivitiesButton';
import { InitialScreen } from './components/UI/InitialScreen';
import { Chat } from './components/Chat/Chat';
import { useWebSocket } from './hooks/useWebSocket';
import { useAvatarAnimation } from './hooks/useAvatarAnimation';
import { useScreenOrientation } from './hooks/useScreenOrientation';
import { loadPolyfills } from './utils/polyfills';
import configService from './services/configService';
import authService from './services/authService';
import webSocketService from './services/webSocketService';
import interactionTimeService from './services/interactionTimeService';
import conversationSaverService from './services/conversationSaverService';
import browserCloseHandler from './services/browserCloseHandler';
import supabaseService from './services/supabaseService';
import academicService from './services/academicService';
import AcademicMode from './components/Academic/AcademicMode';
import { UserConfiguration, PracticeMode } from './types';
import { enableTestAcademicMode } from './utils/testAcademicMode';
import { FeedbackBanner } from './components/UI/FeedbackBanner';
import feedbackService from './services/feedbackService';
import { StreakAchievementModal } from './components/UI/StreakAchievementModal';
import gamificationService from './services/gamificationService';
import TeacherActivityPanel from './components/Teacher/TeacherActivityPanel';
import TeacherActivityForm from './components/Teacher/TeacherActivityForm';
import TeacherActivityPreview from './components/Teacher/TeacherActivityPreview';
import StudentActivityList from './components/Student/StudentActivityList';
import StudentActivityView from './components/Student/StudentActivityView';
import { ActivityEmailForm } from './components/Student/ActivityEmailForm';
import { teacherActivityService } from './services/teacherActivityService';
import { TeacherActivity } from './types';
import { ObjectivesCompactTracker } from './components/UI/ObjectivesCompactTracker';
import { ObjectivesDetailPanel } from './components/UI/ObjectivesDetailPanel';
import './App.css';

// Función helper para determinar promptReference considerando el role del usuario
const getPromptReferenceWithRole = (
  mode: PracticeMode, 
  role: string | undefined | null, 
  isTestingPhase: boolean
): string => {
  const suffix = isTestingPhase ? '-dev' : '-pro';
  
  // Si no hay role, usar lógica por defecto basada solo en el modo
  if (!role) {
    const baseMap: Record<PracticeMode, string> = {
      'conversacional': 'alianzafrancesa-galia-conversacional',
      'estudio': 'alianzafrancesa-galia-ruta-academica',
      'examen': 'alianzafrancesa-galia-examen',
      'actividades-docente': 'alianzafrancesa-galia-actividades-docente',
      'actividades-estudiante': 'alianzafrancesa-galia-actividades-estudiante',
    };
    return `${baseMap[mode]}${suffix}`;
  }
  
  // Mapeo base según el modo
  const baseMap: Record<PracticeMode, string> = {
    'conversacional': 'alianzafrancesa-galia-conversacional',
    'estudio': 'alianzafrancesa-galia-ruta-academica',
    'examen': 'alianzafrancesa-galia-examen',
    'actividades-docente': 'alianzafrancesa-galia-actividades-docente',
    'actividades-estudiante': 'alianzafrancesa-galia-actividades-estudiante',
  };
  
  // Ajustar según el role
  // "Undefined" se trata como estudiante para la interfaz
  const roleLower = role?.toLowerCase() || '';
  const isUndefined = role === 'Undefined' || role === 'undefined';
  const isTeacher = !isUndefined && (roleLower === 'teacher' || roleLower.includes('docente') || roleLower.includes('instructor') || roleLower.includes('profesor'));
  const isStudent = isUndefined || roleLower === 'student' || roleLower.includes('estudiante') || roleLower.includes('learner') || roleLower.includes('alumno');

  // Si el modo es "actividades", forzar el prompt según el role
  if (mode === 'actividades-docente' || mode === 'actividades-estudiante') {
    // Ya está correcto según el modo
    const result = `${baseMap[mode]}${suffix}`;
    return result;
  }
  
  // Para otros modos, ajustar según el role si es necesario
  // Si es teacher y el modo es estudio, podría usar un prompt específico para docentes
  // Por ahora, todos los modos (excepto actividades) usan el mismo prompt independientemente del role
  // Pero podemos agregar lógica específica aquí si es necesario
  
  const result = `${baseMap[mode]}${suffix}`;
  return result;
};

function App() {
  const [shouldConnect, setShouldConnect] = useState(false);
  const { messages, sendAudioMessage, sendTextMessage, isConnecting } = useWebSocket(shouldConnect);
  const { setHablando, resetFirstInteraction } = useAvatarAnimation();

  // Rastrear cambios en shouldConnect para debug
  useEffect(() => {
    if (shouldConnect) {
    }
  }, [shouldConnect]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initialConfigComplete, setInitialConfigComplete] = useState(false);
  const [userConfiguration, setUserConfiguration] = useState<UserConfiguration | null>(null);
  const previousConfigurationRef = useRef<UserConfiguration | null>(null); // Guardar configuración anterior para comparar
  const [isChatVisible, setIsChatVisible] = useState(false);
  const orientation = useScreenOrientation();
  const [showAcademicMode, setShowAcademicMode] = useState(false);
  const [academicLevel, setAcademicLevel] = useState<{ level: string; unit?: number; lesson?: number } | null>(null);
  const [isAcademicProgressVisible, setIsAcademicProgressVisible] = useState(false);
  const [isInformationVisible, setIsInformationVisible] = useState(false);
  const [isLessonInProgress, setIsLessonInProgress] = useState(false);
  const [showFeedbackBanner, setShowFeedbackBanner] = useState(false);
  const [isFeedbackBannerExpanded, setIsFeedbackBannerExpanded] = useState(false);
  
  // Estado para modal de racha de 5
  const [showStreak5Modal, setShowStreak5Modal] = useState(false);
  const [streak5Count, setStreak5Count] = useState(0);
  
  // Estados para sistema de actividades
  const [showTeacherActivityPanel, setShowTeacherActivityPanel] = useState(false);
  const [showTeacherActivityForm, setShowTeacherActivityForm] = useState(false);
  const [showTeacherActivityPreview, setShowTeacherActivityPreview] = useState(false);
  const [showStudentActivityList, setShowStudentActivityList] = useState(false);
  const [showStudentActivityView, setShowStudentActivityView] = useState(false);
  const [currentActivity, setCurrentActivity] = useState<TeacherActivity | null>(null);
  const [editingActivity, setEditingActivity] = useState<TeacherActivity | null>(null);
  
  // Estados para formulario de email cuando se accede por URL
  const [showActivityEmailForm, setShowActivityEmailForm] = useState(false);
  const [activityEmailError, setActivityEmailError] = useState<string | null>(null);
  const [activityEmailLoading, setActivityEmailLoading] = useState(false);

  // Estados para el tracker de objetivos compacto
  const [showObjectivesPanel, setShowObjectivesPanel] = useState(false);
  const [currentLessonObjectives, setCurrentLessonObjectives] = useState<any[]>([]);
  const [currentLessonProgress, setCurrentLessonProgress] = useState<any | null>(null);

  // Email efectivo para InitialScreen: evita que loadUserData (racha, tiempos, interacciones) corra con email vacío
  const [effectiveEmailForInitial, setEffectiveEmailForInitial] = useState<string | null>(null);

  // Mostrar banner de feedback cuando hay una conversación guardable
  // No mostrar si ya se envió feedback en esta sesión
  useEffect(() => {
    const feedbackSent = sessionStorage.getItem('feedback_sent_this_session') === 'true';
    if (initialConfigComplete && messages.length > 0 && messages.some(m => m.role === 'user') && !feedbackSent) {
      setShowFeedbackBanner(true);
    } else if (feedbackSent) {
      setShowFeedbackBanner(false);
    }
  }, [initialConfigComplete, messages]);


  // Verificar si hay una lección en proceso
  const checkLessonInProgress = async () => {
    try {
      const config = configService.getConfig();
      if (!config.email || !academicLevel) {
        setIsLessonInProgress(false);
        return;
      }
      
      // Verificar si el modo académico está activo en WebSocket
      // Esto indica que se inició una lección
      const academicModeActive = (webSocketService as any).academicMode?.isActive;
      
      if (academicModeActive) {
        setIsLessonInProgress(true);
        
        // Cargar objetivos y progreso de la lección activa
        const academicMode = (webSocketService as any).academicMode;
        if (academicMode.level && academicMode.unit !== undefined && academicMode.lesson !== undefined) {
          const objectives = await academicService.getLessonObjectives(
            academicMode.level,
            academicMode.unit,
            academicMode.lesson
          );
          const progress = await academicService.getUserLessonProgress(
            config.email,
            academicMode.level,
            academicMode.unit,
            academicMode.lesson
          );
          
          setCurrentLessonObjectives(objectives);
          setCurrentLessonProgress(progress);
        }
      } else {
        // También verificar si hay progreso en la lección actual sin completar
        const progress = await academicService.getUserLessonProgress(
          config.email,
          academicLevel.level,
          academicLevel.unit || 0,
          academicLevel.lesson || 1
        );
        
        if (progress && !progress.all_objectives_completed) {
          // Verificar si hay algún objetivo con progreso
          const hasProgress = Object.values(progress.objectives_status).some(
            (status: any) => status.correct_count > 0
          );
          setIsLessonInProgress(hasProgress);
          
          if (hasProgress) {
            // Cargar objetivos para mostrar en el tracker
            const objectives = await academicService.getLessonObjectives(
              academicLevel.level,
              academicLevel.unit || 0,
              academicLevel.lesson || 1
            );
            setCurrentLessonObjectives(objectives);
            setCurrentLessonProgress(progress);
          }
        } else {
          setIsLessonInProgress(false);
          setCurrentLessonObjectives([]);
          setCurrentLessonProgress(null);
        }
      }
    } catch {
      setIsLessonInProgress(false);
      setCurrentLessonObjectives([]);
      setCurrentLessonProgress(null);
    }
  };

  // Handler para cambios de visibilidad del chat
  const handleChatVisibilityChange = (isVisible: boolean) => {
    setIsChatVisible(isVisible);
    conversationSaverService.setChatVisibility(isVisible);
  };

  // En modo vertical (portrait), solo cerrar el chat cuando el usuario ABRE un panel (no cuando abre el chat ni por cambios breves de orientación en móvil)
  const prevPanelOpenRef = useRef(false);
  useEffect(() => {
    if (orientation !== 'portrait') {
      // No resetear prevPanelOpenRef en landscape: en móvil el teclado/resize puede cambiar orientation brevemente y cerrar el chat al volver
      return;
    }
    const panelOpen = showAcademicMode || showTeacherActivityPanel || showStudentActivityList || showStudentActivityView;
    // Solo cerrar el chat cuando un panel pasa de cerrado a abierto (el usuario abrió un panel), no cuando el usuario abre el chat
    if (panelOpen && !prevPanelOpenRef.current && isChatVisible) {
      handleChatVisibilityChange(false);
    }
    prevPanelOpenRef.current = panelOpen;
    // No incluir isChatVisible en deps: si lo incluimos, al abrir el chat el efecto se dispara y puede cerrarlo (p. ej. si prevPanelOpenRef quedó false por un resize en móvil)
  }, [orientation, showAcademicMode, showTeacherActivityPanel, showStudentActivityList, showStudentActivityView]);

  useEffect(() => {
    // Inicialización inmediata para que la app cargue al abrir local (no depender de loadPolyfills)
    const envTestingPhase = import.meta.env.VITE_TESTING_PHASE;
    const testingPhase = envTestingPhase !== undefined
      ? (envTestingPhase === 'true' || envTestingPhase === true)
      : (import.meta.env.DEV === true);

    configService.initialize({
      testingPhase: testingPhase,
      testingWebSocket: true,
      useApolearn: true,
      client: 'alianzafrancesa',
      assistantID: 'f613066c-a9ba-47b5-b2db-1d82b38366ed',
      promptReference: `alianzafrancesa-galia-demo-${testingPhase ? 'dev' : 'pro'}`,
    });

    const urlParamsInit = new URLSearchParams(window.location.search);
    const hasTokenInit = urlParamsInit.has('token') || window.location.href.includes('token=');
    const configInit = configService.getConfig();
    // Sin token LTI no se configuran valores por defecto; el correo se pide por formulario y debe ser de Apolearn
    const emailForInitial = configService.getConfig().email || null;
    setEffectiveEmailForInitial(emailForInitial);
    setIsInitialized(true);

    // Polyfills y resto de la lógica (tache_id, LTI, etc.) sin bloquear la carga
    loadPolyfills().then(() => {
      const authPayload = authService.processAuth();
      const urlParams = new URLSearchParams(window.location.search);
      const tacheId = urlParams.get('tache_id');
      if (tacheId) {
        // Si hay tache_id, cargar la actividad. Solo pedir email si aún no hay uno en esta sesión.
        teacherActivityService.getActivityByTacheId(tacheId).then((activity) => {
          if (activity) {
            setCurrentActivity(activity);
            const configForActivity = configService.getConfig();
            if (!configForActivity.email) {
              setShowActivityEmailForm(true);
            } else {
              // Email ya configurado en esta sesión: no pedir de nuevo, abrir actividad directamente
              setShowActivityEmailForm(false);
              setShowStudentActivityView(true);
              setShouldConnect(true);
              setInitialConfigComplete(true);
              const isTestingPhase = configForActivity.testingPhase ?? false;
              const promptRef = getPromptReferenceWithRole('actividades-estudiante', null, isTestingPhase);
              configService.setPromptReference(promptRef);
              configService.setAvatarType('actividades-estudiante');
              configService.setPracticeModeAndAccent('actividades-estudiante', 'Francia');
              const voiceDictionary: Record<string, { voice: string; language: string }> = {
                'Francia': { voice: 'fr-FR-Chirp3-HD-Achernar', language: 'fr-FR' },
                'Canadá': { voice: 'fr-CA-Chirp3-HD-Algenib', language: 'fr-CA' },
                'Australia': { voice: 'fr-FR-Chirp3-HD-Achernar', language: 'fr-FR' },
                'Bélgica': { voice: 'fr-BE-CharlineNeural', language: 'fr-BE' },
              };
              const voiceInfo = voiceDictionary['Francia'];
              setUserConfiguration({
                mode: 'actividades-estudiante',
                promptReference: promptRef,
                rasgo: 'Neutral',
                personalityType: 'Neutral',
                functionalityType: 'Conversadora',
                mcerLevel: 'A1',
                acento: 'Francia',
                voice: voiceInfo.voice,
                language: voiceInfo.language,
              });
              conversationSaverService.setCurrentConfiguration('actividades-estudiante', 'Neutral', 'Francia');
              conversationSaverService.setChatVisibility(false);
            }
          }
        }).catch(() => {
          setActivityEmailError('No se pudo cargar la actividad. Verifica la URL.');
        });
      }

      // Verificar si hay token de LTI
      const hasToken = urlParams.has('token') || window.location.href.includes('token=');
      const hasApolearnData = authService.getApolearnUserData();
      const config = configService.getConfig();
      // Sin token LTI el email se pide por formulario; si no es de Apolearn no se permite ingreso

      // Sincronizar email con estado para que InitialScreen reciba el email y cargue racha/tiempos/interacciones
      const currentEmail = configService.getConfig().email || null;
      setEffectiveEmailForInitial(currentEmail);

      // Si hay token (viene desde Apolearn), esperar a que se obtengan los datos completos
      if (hasToken && authPayload?.email) {
        // Intentar obtener datos completos si aún no están disponibles
        // Usar user_id de LTI Launch si está disponible
        const ltiUserId = parseInt(authPayload.user_id || '0') || 0;
        authService.getApolearnCompleteData(authPayload.email, ltiUserId > 0 ? ltiUserId : undefined)
          .then(apolearnData => {
            
            // Ahora que tenemos los datos completos, intentar obtener el nivel académico
            const config = configService.getConfig();
            const emailToUse = apolearnData.user?.email || authPayload.email || config.email;
            
            if (apolearnData && emailToUse) {
              academicService.getUserAcademicLevel().then(async (level) => {
                if (level) {
                  setAcademicLevel(level);
                  setShowAcademicMode(true);
                  // Verificar si hay una lección en proceso
                  await checkLessonInProgress();
                }
              }).catch(() => {});
            }
          })
          .catch(() => {
            // Intentar con los datos mínimos si están disponibles
            const apolearnData = authService.getApolearnUserData();
            if (apolearnData && apolearnData.user?.email) {
              const config = configService.getConfig();
              const emailToUse = apolearnData.user.email || config.email;
              if (emailToUse) {
                academicService.getUserAcademicLevel().then(async (level) => {
                  if (level) {
                    setAcademicLevel(level);
                    setShowAcademicMode(true);
                  }
                }).catch(() => {});
              }
            }
          });
      }

      // Verificar si hay datos de Apolearn y activar modo académico (para casos sin token)
      const apolearnData = authService.getApolearnUserData();
      // Reutilizar config ya declarado arriba
      
      // Si hay datos de Apolearn pero el email en config no coincide, actualizarlo
      // PRIORIDAD: El email de LTI siempre tiene prioridad sobre cualquier otro email
      if (apolearnData && apolearnData.user?.email) {
        // Si el email en config no coincide con el email de Apolearn, actualizarlo
        if (config.email !== apolearnData.user.email) {
          configService.setUserInfo(
            apolearnData.user.email,
            `${apolearnData.user.firstname || ''} ${apolearnData.user.lastname || ''}`.trim(),
            apolearnData.user.id,
            apolearnData.user.firstname,
            apolearnData.user.lastname
          );
        }
      } else if (authPayload?.email) {
        // Si no hay datos completos de Apolearn pero hay email de LTI, usarlo
        if (config.email !== authPayload.email) {
          configService.setUserInfo(
            authPayload.email,
            authPayload.full_name || '',
            undefined,
            authPayload.full_name?.split(' ')[0],
            authPayload.full_name?.split(' ').slice(1).join(' ')
          );
        }
      }
      
      const updatedConfig = configService.getConfig();
      // Usar el email de Apolearn si está disponible, sino el de LTI, sino el de config
      const emailToUse = apolearnData?.user?.email || authPayload?.email || updatedConfig.email;
      
      // Solo intentar obtener nivel académico si NO hay token y ya hay datos (ya que si hay token, se maneja arriba)
      if (!hasToken && apolearnData && emailToUse) {
        // Obtener nivel académico desde Apolearn
        academicService.getUserAcademicLevel().then(async (level) => {
          if (level) {
            setAcademicLevel(level);
            setShowAcademicMode(true);
            // Verificar si hay una lección en proceso
            await checkLessonInProgress();
          }
        }).catch(() => {});
      }

      // Activar utilidades de prueba (solo en desarrollo)
      if (testingPhase) {
        enableTestAcademicMode();
        
        // Función rápida para activar modo académico de prueba
        if (typeof window !== 'undefined') {
          (window as any).quickTestAcademic = () => {
            setAcademicLevel({ level: 'A1.1', unit: 0, lesson: 1 });
            setShowAcademicMode(true);
          };
          
          /**
           * ACTIVAR MODO ACADÉMICO MANUALMENTE
           * 
           * Esta función activa el modo académico después de simular datos de Apolearn.
           * Se usa cuando ejecutas window.simulateApolearnA1_1() y quieres ver el modo académico
           * sin recargar la página.
           * 
           * Para usar: window.activateAcademicMode()
           */
          (window as any).activateAcademicMode = async () => {
            const apolearnData = authService.getApolearnUserData();
            const config = configService.getConfig();if (!apolearnData) {
              console.error('❌ No hay datos de Apolearn. Usa window.simulateApolearnA1_1() primero.');
              return;
            }
            
            if (!config.email) {
              console.error('❌ No hay email configurado. Configura el email primero.');
              return;
            }
            
            try {const level = await academicService.getUserAcademicLevel();if (level) {setAcademicLevel(level);
                setShowAcademicMode(true);
              } else {
                console.error('❌ No se pudo obtener el nivel académico');
              }
            } catch (error) {console.error('❌ Error al activar modo académico:', error);
            }
          };
          
          /**
           * DESACTIVAR MODO ACADÉMICO
           * 
           * Esta función desactiva el modo académico y oculta el componente.
           * 
           * Para usar: window.deactivateAcademicMode()
           */
          (window as any).deactivateAcademicMode = () => {
            setShowAcademicMode(false);
            setAcademicLevel(null);
          };
        }
      }

        // TEST: Probar obtención de datos de Apolearn con email específico
        // Ejecutar desde consola: window.testApolearn('bryan@lidarit.com')
        if (typeof window !== 'undefined') {
          (window as any).testApolearn = async (email: string) => {
            try {
              const data = await authService.getApolearnCompleteData(email);
              return data;
            } catch (error: any) {
              throw error;
            }
          };
          
          // TEST: Probar conexión con Supabase
          (window as any).testSupabase = async () => {
            try {
              const success = await supabaseService.testConnection();
              return success;
            } catch (error: any) {
              throw error;
            }
          };
          
          // Función para imprimir estadísticas de todos los classrooms del usuario
          // Ejecutar desde consola: window.printClassroomStatistics()
          (window as any).printClassroomStatistics = async () => {
            try {
              await authService.printAllClassroomStatistics();
            } catch (error: any) {
              console.error('Error al imprimir estadísticas:', error);
            }
          };
          
          // Probar conexión automáticamente en modo desarrollo
          if (testingPhase) {
            supabaseService.testConnection().catch(() => {});
          }
        }

      // Las preferencias no se guardan en localStorage por ahora
      // El menú siempre aparecerá al inicio

      // Inicializar tiempo de interacción
      interactionTimeService.initialize();

      // Configurar callbacks del WebSocket
      webSocketService.onAvatarState((isHablando) => {
        setHablando(isHablando);
      });

      // Inicializar handler de cierre del navegador
      browserCloseHandler.initialize();

      setEffectiveEmailForInitial(configService.getConfig().email || null);
      setIsInitialized(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar una vez al montar

  // Guardar conversación periódicamente
  useEffect(() => {
    if (messages.length > 0) {
      const interval = setInterval(() => {
        conversationSaverService.guardarData(
          messages.map((m) => ({ role: m.role, content: m.content, afterInvalidMessage: m.afterInvalidMessage }))
        );
      }, 30000); // Cada 30 segundos

      return () => clearInterval(interval);
    }
  }, [messages]);

  // Actualizar progreso de objetivos en tiempo real cuando hay una lección activa
  useEffect(() => {
    if (!isLessonInProgress || !academicLevel) {
      setCurrentLessonObjectives([]);
      setCurrentLessonProgress(null);
      return;
    }

    const updateProgress = async () => {
      try {
        const config = configService.getConfig();
        if (!config.email) return;

        const academicMode = (webSocketService as any).academicMode;
        if (academicMode?.isActive) {
          const progress = await academicService.getUserLessonProgress(
            config.email,
            academicMode.level,
            academicMode.unit,
            academicMode.lesson
          );
          setCurrentLessonProgress(progress);
        }
      } catch {
        // Error silencioso al actualizar progreso
      }
    };

    // Actualizar cada 2 segundos
    const interval = setInterval(updateProgress, 2000);
    
    return () => clearInterval(interval);
  }, [isLessonInProgress, academicLevel]);

  const handleRecordingComplete = async (audioBlob: Blob) => {
    try {
      // Activar animación de avatar hablando
      setHablando(true);

      // Enviar audio a WebSocket
      await sendAudioMessage(audioBlob);

      // La animación se desactivará cuando termine la respuesta
      // (esto se manejará en el servicio de WebSocket)
    } catch (error) {
      console.error('Error al procesar grabación:', error);
      setHablando(false);
    }
  };

  if (!isInitialized) {
    return (
      <div className="loading-screen">
        <p>Cargando...</p>
      </div>
    );
  }

  const handleReturnToMenu = () => {
    // Guardar la configuración actual antes de resetearla
    if (userConfiguration) {
      previousConfigurationRef.current = userConfiguration;
    }
    // NO desconectar el WebSocket ni cambiar shouldConnect
    // Solo ocultar el menú para permitir que el usuario seleccione elementos nuevamente
    // El WebSocket permanecerá conectado hasta que se presione "Aceptar" con una configuración diferente
    setInitialConfigComplete(false);
    setUserConfiguration(null);
    setShowActivityEmailForm(false); // Resetear el formulario de email si estaba activo
    setCurrentActivity(null); // Limpiar la actividad actual
    setShowStudentActivityView(false); // Cerrar la vista de actividad
    setShowStudentActivityList(false); // Cerrar la lista de actividades
    setShowTeacherActivityPanel(false); // Cerrar el panel de actividades del profesor
    // Mantener shouldConnect como está - NO cambiar a false
    // NO llamar a webSocketService.disconnect()
  };

  // Handler para el formulario de email cuando se accede por URL
  const handleActivityEmailSubmit = async (email: string) => {
    setActivityEmailLoading(true);
    setActivityEmailError(null);

    try {
      // Llamar a getApolearnCompleteData con solo el email (sin userId)
      // Esto obtendrá el usuario por email y luego usará su ID para obtener los datos completos
      const apolearnData = await authService.getApolearnCompleteData(email);
      
      if (!apolearnData || !apolearnData.user) {
        throw new Error('No se pudieron obtener los datos del usuario');
      }

      // Obtener el userId del objeto user retornado
      const userId = apolearnData.user.id;
      
      // Configurar configService con email y userId
      configService.setUserInfo(
        email,
        `${apolearnData.user.firstname || ''} ${apolearnData.user.lastname || ''}`.trim(),
        userId,
        apolearnData.user.firstname,
        apolearnData.user.lastname
      );

      // Obtener role de los datos de Apolearn (puede ser null/undefined)
      const role = apolearnData.role || null;
      const isTestingPhase = configService.getConfig().testingPhase || false;

      // Determinar promptReference (funciona correctamente incluso sin role)
      const promptReference = getPromptReferenceWithRole('actividades-estudiante', role, isTestingPhase);

      // Obtener voice y language según el acento (usando valores por defecto)
      const voiceDictionary: Record<string, { voice: string; language: string }> = {
        'Francia': { voice: 'fr-FR-Chirp3-HD-Achernar', language: 'fr-FR' },
        'Canadá': { voice: 'fr-CA-Chirp3-HD-Algenib', language: 'fr-CA' },
        'Australia': { voice: 'fr-FR-Chirp3-HD-Achernar', language: 'fr-FR' },
        'Bélgica': { voice: 'fr-BE-CharlineNeural', language: 'fr-BE' },
      };
      const accent = 'Francia';
      const voiceInfo = voiceDictionary[accent] || voiceDictionary['Francia'];

      // Configurar automáticamente el modo actividades-estudiante
      const autoConfig: UserConfiguration = {
        mode: 'actividades-estudiante',
        promptReference: promptReference,
        rasgo: 'Neutral',
        personalityType: 'Neutral',
        functionalityType: 'Conversadora',
        mcerLevel: 'A1',
        acento: accent,
        voice: voiceInfo.voice,
        language: voiceInfo.language,
      };

      // Configurar servicios
      configService.setPromptReference(promptReference);
      configService.setAvatarType(autoConfig.mode);
      configService.setPracticeModeAndAccent(autoConfig.mode, autoConfig.acento);
      conversationSaverService.setCurrentConfiguration(autoConfig.mode, autoConfig.rasgo, autoConfig.acento);
      conversationSaverService.setChatVisibility(false);

      // Guardar configuración y completar
      setUserConfiguration(autoConfig);
      setInitialConfigComplete(true);

      // Ocultar formulario y mostrar actividad
      setShowActivityEmailForm(false);
      setShowStudentActivityView(true);

      // Activar conexión WebSocket
      setShouldConnect(true);
    } catch {
      setActivityEmailError('Verifica que sea tu correo de apolearn');
    } finally {
      setActivityEmailLoading(false);
    }
  };

  const handleConfigurationComplete = async (userConfig: UserConfiguration) => {
    // Resetear el estado de primera interacción para que la próxima vez muestre greeting
    resetFirstInteraction();
    
    // Usar userConfiguration si está disponible, sino usar previousConfigurationRef
    const configToCompare = userConfiguration || previousConfigurationRef.current;
    
    // Comparar con la configuración anterior para determinar si es la misma
    const isSameConfiguration = configToCompare && 
      configToCompare.mode === userConfig.mode &&
      configToCompare.rasgo === userConfig.rasgo &&
      configToCompare.acento === userConfig.acento;
    
    const hasPreviousConfig = !!configToCompare;
    
    // Si la configuración es diferente Y hay una configuración previa, guardar datos anteriores y limpiar
    if (!isSameConfiguration && hasPreviousConfig) {
      // Obtener mensajes actuales antes de limpiar
      const currentMessages = webSocketService.getChatHistory();
      
      // Si hay mensajes, enviarlos al lambda antes de limpiar
      if (currentMessages.length > 0) {
        const finalMessages = currentMessages.map((m) => ({
          role: m.role,
          content: m.content,
          afterInvalidMessage: m.afterInvalidMessage,
        }));
        
        try {
          await conversationSaverService.guardarData(finalMessages);
        } catch {
          // Error silencioso al guardar datos anteriores
        }
      }
      
      // Limpiar todas las colas de audio y mensajes pendientes
      webSocketService.clearAllQueues();
      
      // Limpiar historial de chat y resetear estado
      webSocketService.clearChatHistory();
      conversationSaverService.resetConversationState();
      
      // Desconectar WebSocket para reiniciar con nueva configuración
      webSocketService.disconnect();
      setShouldConnect(false);
      
      // Esperar un poco para que la desconexión se complete antes de continuar
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    // Si la configuración no cambió, no volver a setear promptReference ni reconectar
    if (isSameConfiguration) {
      setUserConfiguration(userConfig);
      setInitialConfigComplete(true);
      previousConfigurationRef.current = null;
      if (!shouldConnect) setShouldConnect(true);
      return;
    }
    
    // Esperar a que los datos de Apolearn estén disponibles antes de setear promptReference
    // El promptReference depende del role del usuario
    let apolearnData = authService.getApolearnUserData();
    let attempts = 0;
    const maxAttempts = 10; // 5 segundos máximo (500ms * 10)
    
    while (!apolearnData || !apolearnData.role) {
      if (attempts >= maxAttempts) {
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      apolearnData = authService.getApolearnUserData();
      attempts++;
    }

    // Determinar promptReference considerando el role
    const role = apolearnData?.role;
    const config = configService.getConfig();
    const isTestingPhase = config.testingPhase ?? true;

    const promptReference = getPromptReferenceWithRole(userConfig.mode, role, isTestingPhase);

    // Actualizar promptReference según el modo y role
    configService.setPromptReference(promptReference);
    
    // Guardar configuración de avatar
    configService.setAvatarType(userConfig.mode);
    
    // Guardar modo y acento en configService para que webSocketService pueda usarlos
    configService.setPracticeModeAndAccent(userConfig.mode, userConfig.acento);
    
    // Establecer configuración en conversationSaverService
    conversationSaverService.setCurrentConfiguration(userConfig.mode, userConfig.rasgo, userConfig.acento);
    
    // Inicializar rastreo del chat (inicialmente cerrado)
    conversationSaverService.setChatVisibility(false);
    
    // Guardar configuración completa
    setUserConfiguration(userConfig);
    setInitialConfigComplete(true);
    
    // El banner de feedback se mostrará automáticamente cuando haya mensajes
    // (se verifica dinámicamente en el render)
    
    // Limpiar el ref de configuración anterior DESPUÉS de guardar la nueva configuración
    // Esto permite que la próxima vez que se presione "Home" y luego "Aceptar", se pueda comparar correctamente
    previousConfigurationRef.current = null;
    
    // Activar conexión WebSocket después de completar configuración
    // Siempre reconectar cuando la configuración cambia o es la primera vez
    if (!isSameConfiguration) {
      if (hasPreviousConfig) {
        // Configuración diferente: ya se desconectó arriba, ahora reconectar
        setTimeout(() => {
          setShouldConnect(true);
        }, 200);
      } else {
        // Primera vez: conectar directamente
        setShouldConnect(true);
      }
    } else {
      // Misma configuración: solo conectar si no está conectado
      if (!shouldConnect) {
        setShouldConnect(true);
      }
    }

    // Los mensajes de bienvenida predeterminados han sido eliminados
    // El modo actividades inicia directamente sin mensajes automáticos
    
    // Si hay datos de Apolearn, reactivar el modo académico después de la configuración
    // (apolearnData ya está disponible desde arriba)
    
    // Asegurar que el email en configService coincida con el de Apolearn
    if (apolearnData && apolearnData.user?.email && config.email !== apolearnData.user.email) {
      configService.setUserInfo(
        apolearnData.user.email,
        `${apolearnData.user.firstname || ''} ${apolearnData.user.lastname || ''}`.trim(),
        apolearnData.user.id,
        apolearnData.user.firstname,
        apolearnData.user.lastname
      );
    }
    
    const updatedConfig = configService.getConfig();
    if (apolearnData && updatedConfig.email) {
      academicService.getUserAcademicLevel().then(async (level) => {
        if (level) {
          setAcademicLevel(level);
          setShowAcademicMode(true);
          // Verificar si hay una lección en proceso
          await checkLessonInProgress();
        }
      }).catch(() => {});
    }
  };

  return (
    <LayoutManager>
      <div className={`app-container ${isAcademicProgressVisible ? 'academic-sidebar-visible' : ''} ${showTeacherActivityPanel ? 'teacher-activity-sidebar-visible' : ''} ${showStudentActivityList || showStudentActivityView ? 'student-activity-sidebar-visible' : ''} ${isInformationVisible ? 'information-visible' : ''}`}>
        {(() => {return null;
        })()}
        {/* Formulario de email cuando se accede por URL con tache_id */}
        {showActivityEmailForm && (
          <ActivityEmailForm
            onSubmit={handleActivityEmailSubmit}
            error={activityEmailError}
            loading={activityEmailLoading}
          />
        )}

        {/* Menú inicial solo si no está completada la configuración y no se está mostrando el formulario de email */}
        {!initialConfigComplete && !showActivityEmailForm && (
          <>
            <InitialScreen
              initialEmail={effectiveEmailForInitial}
              onConfigurationComplete={handleConfigurationComplete}
            />
          </>
        )}

        {(() => {return null;
        })()}
        {(() => {const shouldRender = initialConfigComplete && userConfiguration;
          const config = configService.getConfig();if (!shouldRender) {return null;
          }return (
            <>
            <LottieAvatar 
              className={`main-avatar ${isChatVisible ? 'chat-visible' : ''}`}
              mode={userConfiguration.mode}
              avatarId="avatar1"
            />
            
            {/* Tracker de objetivos compacto - mostrar cuando hay una lección activa */}
            {isLessonInProgress && currentLessonObjectives.length > 0 && (
              <div className="objectives-tracker-container">
                <ObjectivesCompactTracker
                  objectives={currentLessonObjectives}
                  progress={currentLessonProgress}
                  onInfoClick={() => setShowObjectivesPanel(true)}
                />
              </div>
            )}
            
            {/* Iconos en las esquinas */}
            <img 
              src="/assets/ui/otros/galia.png" 
              alt="Galia" 
              className="corner-icon corner-icon-top-left"
            />
            <img 
              src="/assets/ui/otros/logo_af.png" 
              alt="Alianza Francesa" 
              className="corner-icon corner-icon-top-right"
            />

            {/* Modo Académico - Mantener montado para preservar estado */}
            {/* Panel siempre disponible cuando se hace clic en el botón */}
            {isAcademicProgressVisible && (
              <div className={`academic-mode-container ${isAcademicProgressVisible ? 'visible' : 'hidden'}`}>
                {config.email ? (
                  <AcademicMode 
                    userEmail={config.email} 
                    showProgressMap={true}
                    onClose={() => {
                      // Solo minimizar el panel, no resetear estado
                      setIsAcademicProgressVisible(false);
                      // Verificar si hay una lección en proceso de forma asíncrona sin bloquear
                      checkLessonInProgress().catch(console.error);
                    }}
                    onActivityStart={() => setIsLessonInProgress(true)}
                    onActivityEnd={() => setIsLessonInProgress(false)}
                  />
                ) : (
                  <div className="academic-mode-no-data">
                    <p>Para acceder a la ruta académica, necesitas iniciar sesión con tu cuenta de Apolearn.</p>
                    <button onClick={() => setIsAcademicProgressVisible(false)}>Cerrar</button>
                  </div>
                )}
              </div>
            )}
            </>
          );
        })()}
        
        <div className={`ui-controls ${isFeedbackBannerExpanded ? 'hidden' : ''}`}>
          <div className="ui-controls-left">
            <MicrophoneButton onRecordingComplete={handleRecordingComplete} />
            <Chat 
              messages={messages} 
              onSendMessage={sendTextMessage}
              inline={true}
              isVisibleControlled={isChatVisible}
              onVisibilityChange={handleChatVisibilityChange}
              mode={userConfiguration?.mode}
              enableCopyPaste={true} // Habilitado para todos los modos, especialmente útil en modo estudio
              enableAudioReplay={true} // Habilitado para todos los modos
            />
          </div>
          <div className="ui-controls-right">
            {/* Ocultar botones de la derecha cuando está abierto el panel de ruta académica o el de actividades (solo quedan mic y chat) */}
            {!isAcademicProgressVisible && !showTeacherActivityPanel && !showStudentActivityList && !showStudentActivityView && (
              <>
                {/* Botón de ruta académica siempre visible */}
                <AcademicProgressButton 
                  onClick={() => setIsAcademicProgressVisible(true)}
                  isInProgress={isLessonInProgress}
                />
                {/* Botón de actividades siempre visible */}
                <ActivitiesButton
                  onClick={() => {
                    // Determinar qué panel mostrar según el role del usuario
                    const apolearnData = authService.getApolearnUserData();
                    const userRole = apolearnData?.role || null;
                    const roleLower = userRole?.toLowerCase() || '';
                    const isUndefined = userRole === 'Undefined' || userRole === 'undefined';
                    const isDocente = !isUndefined && (roleLower === 'teacher' || roleLower.includes('docente') || roleLower.includes('instructor') || roleLower.includes('profesor'));

                    // Cerrar otros paneles primero
                    setShowTeacherActivityPanel(false);
                    setShowStudentActivityList(false);
                    setShowStudentActivityView(false);
                    
                    // Si hay una actividad cargada, mostrar la vista de actividad
                    if (currentActivity) {
                      setShowStudentActivityView(true);
                    } else if (isDocente) {
                      setShowTeacherActivityPanel(true);
                    } else {
                      setShowStudentActivityList(true);
                    }
                  }}
                  title={currentActivity ? "Ver Actividad" : "Mis Actividades"}
                />
                <InfoButton onToggle={setIsInformationVisible} />
                
              </>
            )}
          </div>
        </div>

        {isConnecting && shouldConnect && (
          <div className="connection-status">
            <div className="connection-spinner"></div>
            <p>Conectando con el servidor...</p>
          </div>
        )}
      </div>

      {/* Banner de feedback - solo mostrar si hay conversación guardable */}
      {showFeedbackBanner && initialConfigComplete && messages.length > 0 && messages.some(m => m.role === 'user') && (
        <FeedbackBanner
          isVisible={true}
          onSubmit={async (rating, feedback) => {
            await feedbackService.sendFeedback(rating, feedback);
            // Asegurar que los botones reaparezcan
            setIsFeedbackBannerExpanded(false);
            setShowFeedbackBanner(false);
          }}
          onExpandChange={(isExpanded) => {
            setIsFeedbackBannerExpanded(isExpanded);
            // Cerrar el chat cuando el banner se expande
            if (isExpanded && isChatVisible) {
              handleChatVisibilityChange(false);
            }
          }}
        />
      )}

      {/* Componentes de actividades del profesor */}
      {showTeacherActivityPanel && (
        <div className={`teacher-activity-container ${showTeacherActivityPanel ? 'visible' : 'hidden'}`}>
          <TeacherActivityPanel
            onClose={() => setShowTeacherActivityPanel(false)}
            onNewActivity={() => {
              setShowTeacherActivityPanel(false);
              setEditingActivity(null);
              setShowTeacherActivityForm(true);
            }}
            onEditActivity={(activity) => {
              setShowTeacherActivityPanel(false);
              setEditingActivity(activity);
              setShowTeacherActivityForm(true);
            }}
          />
        </div>
      )}

      {showTeacherActivityForm && (
        <TeacherActivityForm
          activity={editingActivity}
          onSave={(activity) => {
            setShowTeacherActivityForm(false);
            setEditingActivity(null);
            setCurrentActivity(activity);
            setShowTeacherActivityPreview(true);
          }}
          onCancel={() => {
            setShowTeacherActivityForm(false);
            setEditingActivity(null);
            setShowTeacherActivityPanel(true);
          }}
        />
      )}

      {showTeacherActivityPreview && currentActivity && (
        <TeacherActivityPreview
          activity={currentActivity}
          onAdjust={() => {
            setShowTeacherActivityPreview(false);
            setEditingActivity(currentActivity);
            setShowTeacherActivityForm(true);
          }}
          onDeployed={(deployedActivity) => {
            setShowTeacherActivityPreview(false);
            setCurrentActivity(null);
            // Mostrar URL en el chat
            if (deployedActivity.tache_id) {
              const url = teacherActivityService.generateActivityUrl(deployedActivity.tache_id);
              const urlMessage = `✅ Actividad desplegada exitosamente!\n\nURL para compartir:\n${url}\n\nCopia esta URL y compártela con tus estudiantes.`;
              webSocketService.addSystemMessage(urlMessage);
            }
            setShowTeacherActivityPanel(true);
          }}
          onCancel={() => {
            setShowTeacherActivityPreview(false);
            setCurrentActivity(null);
            setShowTeacherActivityPanel(true);
          }}
        />
      )}

      {/* Componentes de actividades del estudiante */}
      {showStudentActivityList && (
        <div className={`student-activity-container ${showStudentActivityList ? 'visible' : 'hidden'}`}>
          <StudentActivityList
            onSelectActivity={(activity) => {
              setShowStudentActivityList(false);
              setCurrentActivity(activity);
              setShowStudentActivityView(true);
            }}
            onClose={() => setShowStudentActivityList(false)}
          />
        </div>
      )}

      {showStudentActivityView && currentActivity && (
        <div className={`student-activity-view-container ${showStudentActivityView ? 'visible' : 'hidden'}`}>
          <StudentActivityView
            activity={currentActivity}
            onStartActivity={() => {
              // NO cerrar la vista de actividad ni cambiar al panel de progreso
              // Solo iniciar la actividad (ya se hace en startTeacherActivity)
              // El modo académico se activa internamente pero no mostramos el panel de progreso
              // La vista de actividad se mantiene visible
            }}
            onBack={() => {
              // Volver a la lista de actividades
              setShowStudentActivityView(false);
              setShowStudentActivityList(true);
            }}
            onClose={() => {
              // Solo ocultar la vista, pero mantener la actividad cargada
              // para que el botón pueda volver a abrirla
              setShowStudentActivityView(false);
            }}
          />
        </div>
      )}

      {/* Panel de detalles de objetivos */}
      {showObjectivesPanel && currentLessonObjectives.length > 0 && (
        <ObjectivesDetailPanel
          objectives={currentLessonObjectives}
          progress={currentLessonProgress}
          onClose={() => setShowObjectivesPanel(false)}
          isOpen={showObjectivesPanel}
        />
      )}
    </LayoutManager>
  );
}

export default App;
